import React, { useState } from 'react';
import { MathJax } from 'better-react-mathjax';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { motion, AnimatePresence } from 'framer-motion';
import { renderBlocks } from '../components/renderBlocks';

const MarkdownWithMath = ({ children }) => (
  <MathJax dynamic>
    <ReactMarkdown remarkPlugins={[remarkGfm]}>{children}</ReactMarkdown>
  </MathJax>
);

export default function AccordionQA({ items, onModalLinkClick }) {
  const [openIndex, setOpenIndex] = useState(null);

  const toggleItem = (index) => {
    setOpenIndex(openIndex === index ? null : index);
  };

  if (!items || items.length === 0) return <div>No items found</div>;

  return (
    <div className="max-w-4xl mx-auto mt-6 space-y-4">
      {items.map((item, index) => {
        const questionImages = item.image_links?.filter(link => link.tag === 'question') || [];
        const answerImages = item.image_links?.filter(link => link.tag === 'answer') || [];

        return (
          <div
            key={item.id || index}
            className="border border-indigo-200 rounded-xl shadow-sm overflow-hidden"
          >
            <button
              className="w-full text-left px-6 py-4 bg-gradient-to-r from-indigo-50 to-indigo-100 hover:from-indigo-100 hover:to-indigo-200 transition-colors duration-300"
              onClick={() => toggleItem(index)}
            >
              <div className="text-md font-semibold text-indigo-700 space-y-2">
                <div className="flex items-baseline gap-2">
                  <span className="whitespace-nowrap">Q{index + 1}:</span>
                  <div className="flex-1">
                    {item.question_blocks
                      ? renderBlocks(item.question_blocks, onModalLinkClick)
                      : <MarkdownWithMath>{item.question}</MarkdownWithMath>}
                  </div>
                </div>

                {questionImages.length > 0 && (
                  <div className="flex flex-wrap justify-start gap-3 mt-2">
                    {questionImages.map((img, idx) => (
                      <img
                        key={idx}
                        src={img.qa_images?.image_url}
                        alt={img.qa_images?.label || 'image'}
                        style={{
                          width: `${img.qa_images?.width_percent || 100}%`,
                          maxWidth: '100%'
                        }}
                        className="h-auto"
                      />
                    ))}
                  </div>
                )}
              </div>
            </button>

            <AnimatePresence initial={false}>
              {openIndex === index && (
                <motion.div
                  key="content"
                  initial={{ height: 0, opacity: 0 }}
                  animate={{ height: 'auto', opacity: 1 }}
                  exit={{ height: 0, opacity: 0 }}
                  transition={{ duration: 0.4, ease: 'easeInOut' }}
                  className="px-6 pt-1 pb-4 bg-white"
                >
                  <div className="text-gray-800 leading-relaxed">
                    <p className="text-base font-medium mb-2">Answer:</p>

                    {answerImages.length > 0 && (
                      <div className="mb-3 flex flex-wrap justify-start gap-3">
                        {answerImages.map((img, idx) => (
                          <img
                            src={img.qa_images?.image_url}
                            alt={img.qa_images?.label || 'image'}
                            style={{
                              width: `${img.qa_images?.width_percent || 100}%`,
                              maxWidth: '100%',
                              minWidth: '300px'
                            }}
                            className="h-auto"
                          />
                        ))}
                      </div>
                    )}

                    <div className="text-base text-gray-700 bg-indigo-50 p-3 rounded shadow-inner">
                      {item.answer_blocks
                        ? renderBlocks(item.answer_blocks, onModalLinkClick)
                        : <MarkdownWithMath>{item.answer}</MarkdownWithMath>}
                    </div>

                    {item.hint && (
                      <p className="mt-3 text-sm italic text-indigo-500">
                        <MathJax dynamic>
                          💡<span><strong>Hint:</strong> </span>{item.hint}
                        </MathJax>
                      </p>
                    )}
                  </div>
                </motion.div>
              )}
            </AnimatePresence>
          </div>
        );
      })}
    </div>
  );
}
